/* eslint-disable react-hooks/exhaustive-deps */
import { Elements, useStripe } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js/pure";
import { ChevronRight } from "lucide-react";
import { useRouter } from "next/compat/router";
import { type JSX, useEffect, useState } from "react";
import { FaCheckCircle, FaInfoCircle } from "react-icons/fa";

import LayoutCard from "~/components/LayoutCard";
import LoadingAnimation from "~/components/LoadingAnimation";
import { SecondaryLayout } from "~/layouts";
import type { Layout, NextPageWithProps } from "~/types";

const stripePromise = loadStripe(
	process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY!,
);

const AddNewPaymentMethodProcessingPage: NextPageWithProps = () => {
	const { setup_intent_client_secret } = useRouter()?.query ?? {};
	const stripe = useStripe();

	const clientSecret = setup_intent_client_secret as string;

	const router = useRouter();

	const isSignUp = router?.query?.mode === "sign-up";

	const processStates: {
		[key: string]: {
			title: string;
			subtitle: string;
			icon: JSX.Element;
			link?: {
				href: string;
				text: string;
			};
		};
	} = {
		loading: {
			title: `Processing`,
			subtitle: `Please wait while we process your card`,
			icon: <LoadingAnimation className="py-4" />,
			link: undefined,
		},
		success: {
			title: `Success`,
			subtitle: `You have been given free trial access to the platform.`,
			icon: <FaCheckCircle className="mx-auto text-[7rem] text-emerald-500" />,
			link: {
				href: isSignUp ? "/select-a-profession?mode=sign-up" : `/`,
				text: `Go to dashboard`,
			},
		},
		error: {
			title: `Payment failed`,
			subtitle: `Something went wrong while processing your payment. Please try again.`,
			link: {
				href: isSignUp ? `trial-membership?mode=sign-up` : `/trial-membership`,
				text: `Try again`,
			},

			icon: <FaInfoCircle className="mx-auto text-[7rem] text-amber-400" />,
		},
	};

	const [processState, setProcessState] = useState(processStates.loading);

	useEffect(() => {
		if (!stripe) {
			return;
		}

		if (!clientSecret) return;

		stripe.retrieveSetupIntent(clientSecret).then(({ setupIntent }) => {
			switch (setupIntent!.status) {
				case `succeeded`:
					setProcessState(processStates.success);
					break;
				case `processing`:
					setProcessState(processStates.loading);
					break;
				default:
					setProcessState({
						...processStates.error,
						subtitle: setupIntent!.cancellation_reason!,
					});
			}
		});
	}, [
		clientSecret,
		stripe,
		processStates.error,
		processStates.loading,
		processStates.success,
	]);

	return (
		<LayoutCard title={processState.title} subtitle={processState.subtitle}>
			<div className="py-20">{processState.icon}</div>
			{processState.link && (
				<a
					href={processState.link!.href}
					className="flex items-center justify-center text-slate-600 hover:text-slate-700 hover:underline dark:text-slate-300 dark:hover:text-slate-200"
				>
					{processState.link?.text} <ChevronRight className="h-5 w-5" />
				</a>
			)}
		</LayoutCard>
	);
};

const Layout: Layout = ({ children }) => (
	<SecondaryLayout>
		<Elements stripe={stripePromise}>{children}</Elements>
	</SecondaryLayout>
);

AddNewPaymentMethodProcessingPage.Layout = Layout;

export default AddNewPaymentMethodProcessingPage;
